import { iChainBase } from "../../core/models/ChainBase";
import { iTableConfigItem } from "../../components/new/Table/Table.helpers";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { getFormattedThousand, getFormattedThousandWithDecimal, highlightMatches } from "../../core/helpers";
import { routeNames } from "../../core/routeNames";
import { ColumnIconType } from "../../components/new/Table/useTableSort";
import { DEFAULT_FILTER_TITLE } from "../../core/models/ChainsFilters";

type ColumnsWidthList = Partial<Record<keyof iChainBase, number>>;

const COLUMNS_WIDTH_WITHOUT_MENU_COUNT: ColumnsWidthList = {
    name: 21,
    metroArea: 12,
    stateFullName: 11,
    sales: 12,
    numberOfUnits: 8,
    unitsGrowth: 9,
    segment: 14,
    menuType: 13,
};

const COLUMNS_WIDTH_WITH_MENU_COUNT: ColumnsWidthList = {
    name: 18,
    metroArea: 11,
    stateFullName: 10,
    sales: 12,
    numberOfUnits: 8,
    unitsGrowth: 8,
    segment: 11,
    menuType: 12,
    menuItemCount: 10,
};

function getMenuCountTooltip(includes: string[], excludes: string[]): string {
    const quote = (terms: string[]) => terms.map(t => `"${t}"`).join(', ');
    const lines = ['Number of items containing:'];
    if (includes.length) lines.push(`Include: ${quote(includes)}`);
    if (excludes.length) lines.push(`Exclude: ${quote(excludes)}`);
    return lines.join('\n');
}

export function useTableConfig(
    places: iChainBase[],
    sortingField: keyof iChainBase | null,
    sortingDirection: 'asc' | 'desc',
    handleSort: (fieldName: keyof iChainBase) => () => void,
    getColumnIcon: ColumnIconType,
    searchQuery: string,
    isMenuCountColumnVisible: boolean = false,
    includes: string[] = [],
    excludes: string[] = [],
): iTableConfigItem[] {
    const columnIcon = useCallback(
        (fieldName: keyof iChainBase) => getColumnIcon(fieldName, sortingField, sortingDirection),
        [getColumnIcon, sortingDirection, sortingField]
    );

    return useMemo((): iTableConfigItem[] => {
        const columnsWidthList = isMenuCountColumnVisible
            ? COLUMNS_WIDTH_WITH_MENU_COUNT
            : COLUMNS_WIDTH_WITHOUT_MENU_COUNT;

        const columns: iTableConfigItem[] = [
        {
            styles: {
                cell: {
                    paddingLeft: 0,
                    flexBasis: `${columnsWidthList['name']}%`,
                },
            },
            title: 'Chain',
            icon: columnIcon('name'),
            isActive: sortingField === 'name',
            Value: ({ index }) => (
                <Link
                    className="NewTopChains__link"
                    to={{
                        pathname: routeNames.chainDetails,
                        search: `id=${places[index].id}`,
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    dangerouslySetInnerHTML={{
                        __html: highlightMatches(
                            places[index].name,
                            searchQuery,
                            (match) => `<b class="table__data-highlight">${match}</b>`
                        ),
                    }}
                />
            ),
            handleHeaderClick: handleSort("name"),
        },
        {
            styles: {
                cell: {
                    flexBasis: `${columnsWidthList['metroArea']}%`,
                },
            },
            title: 'HQ Metro',
            icon: columnIcon('metroArea'),
            isActive: sortingField === 'metroArea',
            Value: ({ index }) => <>{places[index].metroArea}</>,
            handleHeaderClick: handleSort("metroArea"),
        },
        {
            styles: {
                cell: {
                    flexBasis: `${columnsWidthList['stateFullName']}%`,
                },
            },
            title: 'HQ State',
            icon: columnIcon('stateFullName'),
            isActive: sortingField === 'stateFullName',
            Value: ({ index }) => <>{places[index].stateFullName}</>,
            handleHeaderClick: handleSort("stateFullName"),
        },
        {
            styles: {
                cell: {
                    flexBasis: `${columnsWidthList['sales']}%`,
                },
            },
            title: 'Annual Sales ($mm)',
            icon: columnIcon('sales'),
            isActive: sortingField === 'sales',
            textCentered: true,
            Value: ({ index }) => {
                const value = places[index].sales;
                return (
                    <>{value ? `$${getFormattedThousandWithDecimal(value / 10 ** 6, 1)}` : DEFAULT_FILTER_TITLE}</>
                );
            },
            handleHeaderClick: handleSort("sales"),
        },
        {
            styles: {
                cell: {
                    flexBasis: `${columnsWidthList['numberOfUnits']}%`,
                },
            },
            title: 'Current Units',
            icon: columnIcon('numberOfUnits'),
            isActive: sortingField === 'numberOfUnits',
            textCentered: true,
            Value: ({ index }) => (
                <>{getFormattedThousand(places[index].numberOfUnits)}</>
            ),
            handleHeaderClick: handleSort("numberOfUnits"),
        },
        {
            styles: {
                cell: {
                    flexBasis: `${columnsWidthList['unitsGrowth']}%`,
                },
            },
            title: 'Unit Growth (1yr)',
            icon: columnIcon('unitsGrowth'),
            isActive: sortingField === 'unitsGrowth',
            textCentered: true,
            Value: ({ index }) => {
                const val = places[index].unitsGrowth;
                let renderValue = DEFAULT_FILTER_TITLE;
                if (val !== null) {
                    renderValue = `${val > 0 ? "+" : ''}${getFormattedThousand(val)}`;
                }

                return (
                    <span>
                        {renderValue}
                    </span>
                )
            },
            handleHeaderClick: handleSort("unitsGrowth"),
        },
        {
            styles: {
                cell: {
                    flexBasis: `${columnsWidthList['segment']}%`,
                },
            },
            title: 'Segment',
            icon: columnIcon('segment'),
            isActive: sortingField === 'segment',
            Value: ({ index }) => <>{places[index].segment}</>,
            handleHeaderClick: handleSort("segment"),
        },
        {
            styles: {
                cell: {
                    flexBasis: `${columnsWidthList['menuType']}%`,
                },
            },
            title: 'Menu Type',
            icon: columnIcon('menuType'),
            isActive: sortingField === 'menuType',
            Value: ({ index }) => <>{places[index].menuType}</>,
            handleHeaderClick: handleSort("menuType"),
        },
        ];

        if (isMenuCountColumnVisible) {
            columns.push({
                styles: {
                    cell: {
                        flexBasis: `${columnsWidthList['menuItemCount']}%`,
                    },
                },
                columnClassName: 'NewTopChains__menu-count-cell',
                title: '# of items',
                tooltip: getMenuCountTooltip(includes, excludes),
                tooltipClassName: 'TableNew__tooltip--menu-count',
                icon: columnIcon('menuItemCount'),
                isActive: sortingField === 'menuItemCount',
                textCentered: true,
                Value: ({ index }) => {
                    const count = places[index].menuItemCount;
                    if (count == null) return <>-</>;
                    if (count === 0) return <>0</>;
                    return (
                        <Link
                            className="NewTopChains__link NewTopChains__menu-count-link"
                            to={{
                                pathname: routeNames.chainDetails,
                                search: `id=${places[index].id}&menuIncludes=${encodeURIComponent(includes.join(','))}`,
                                hash: '#menu',
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {getFormattedThousand(count)}
                        </Link>
                    );
                },
                handleHeaderClick: handleSort("menuItemCount"),
            });
        }

        return columns;
    }, [columnIcon, handleSort, places, sortingField, searchQuery, isMenuCountColumnVisible, includes, excludes])
}